import { AddCircle, CloseCircle } from "iconsax-react-nativejs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FlatList, Modal, Pressable, Text, View } from "react-native";
import ItemRow, { SelectableItem } from "./item-row";
import ScButton from "../../ui-kit/button";
import ScSearchInput from "../../ui-kit/search-bar";
import { useTheme } from "@/src/styles/theme-provider";
import ItemSelectorList from "./item-selector-list";

type ItemSelectorModalProps = Readonly<{
  items?: SelectableItem[];
  selectedItems?: SelectableItem[];
  visible: boolean;
  onClose: () => void;
  onConfirm?: (ids: string[], items: SelectableItem[]) => void;
  titleKey?: string;
  confirmButtonKey?: string;
  searchPlaceholderKey?: string;
  filterFn?: (item: SelectableItem, search: string) => boolean;
}>;

const defaultFilterFn = (item: SelectableItem, search: string) => {
  const lower = search.toLowerCase();
  return (
    item.displayName?.toLowerCase().includes(lower) ??
    item.userName?.toLowerCase().includes(lower) ??
    false
  );
};

const ItemSelectorModal = ({
  items = [],
  selectedItems = [],
  visible,
  onClose,
  onConfirm,
  titleKey = "LABEL_INVITE_USER",
  confirmButtonKey = "BTN_INVITE",
  searchPlaceholderKey = "LABEL_SEARCH",
  filterFn = defaultFilterFn,
}: ItemSelectorModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<SelectableItem[]>(selectedItems);

  useEffect(() => {
    if (visible) {
      setSelected(selectedItems);
    }
  }, [visible, selectedItems]);

  const selectedIds = selected.map((item) => item.itemId);

  const toggle = (item: SelectableItem) => {
    setSelected((prev) =>
      prev.some((itm) => itm.itemId === item.itemId)
        ? prev.filter((itm) => itm.itemId !== item.itemId)
        : [...prev, item],
    );
  };

  const handleConfirm = () => {
    onConfirm?.(selectedIds, selected);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View
        className="flex-1 items-center justify-center px-4 bg-black/60"
      >
        <View
          style={themeVars as any}
          className="w-full max-w-md h-[80%] bg-chalk rounded-2xl overflow-hidden"
        >
          {/* Header */}
          <View className="flex-row justify-between items-center gap-3 px-5 pt-5 pb-4 border-b border-ash-3">
            <Text className="text-base text-onyx">{t(titleKey)}</Text>
            <Pressable onPress={onClose} className="p-1.5 rounded-full hover:bg-ash-3">
              <CloseCircle size={20} color="var(--color-ash-1)" />
            </Pressable>
          </View>

          {/* List */}
          <ItemSelectorList
            items={items}
            selectedIds={selectedIds}
            searchPlaceholderKey={searchPlaceholderKey}
            filterFn={filterFn}
            onToggle={toggle}
            onClear={() => setSelected([])}
          />

          {/* Footer */}
          <View className="flex-row items-center justify-end gap-3 px-5 py-4 border-t border-gray-100">
            <ScButton text="BTN_CANCEL" mode="outlined" onPress={onClose} />
            <ScButton
              text={confirmButtonKey}
              onPress={handleConfirm}
              icon={<AddCircle size={18} color="white" />}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ItemSelectorModal;
